import { useEffect, useState } from 'react';
import { asset } from '../assets';
import { supabaseClient } from '../supabase';
import { useContactForm } from './ContactFormProvider';
import { useLanguage } from './LanguageProvider';

const fallbackSlides = [
  {
    id: 'fallback',
    image_url: asset('assets/hero.png'),
    headline_en: 'INGAZ Print',
    headline_ar: 'إنجاز للطباعة',
    subheading_en: 'Premium printing made simple',
    subheading_ar: 'طباعة احترافية بكل سهولة',
    bullet1_text_en: 'Fast turnaround for business print orders',
    bullet1_text_ar: 'تنفيذ سريع لطلبات طباعة الأعمال',
    bullet1_icon: 'fa-solid fa-gauge-high',
    bullet2_text_en: 'High quality digital and offset printing',
    bullet2_text_ar: 'طباعة رقمية وأوفست عالية الجودة',
    bullet2_icon: 'fa-solid fa-gem',
    cta_primary_text_en: 'Get a Quote',
    cta_primary_text_ar: 'احصل على عرض سعر',
    cta_primary_link: '#products',
    cta_secondary_text_en: 'Contact Us',
    cta_secondary_text_ar: 'اتصل بنا',
    cta_secondary_link: '#quote',
  },
];

function iconClass(icon) {
  const icons = {
    fast: 'fa-solid fa-gauge-high',
    time: 'fa-solid fa-clock',
    hammer: 'fa-solid fa-hammer',
    quality: 'fa-solid fa-gem',
    star: 'fa-solid fa-star',
    truck: 'fa-solid fa-truck-fast',
    check: 'fa-solid fa-circle-check',
    'check-circle': 'fa-solid fa-circle-check',
    delivery: 'fa-solid fa-truck-fast',
    print: 'fa-solid fa-print',
    design: 'fa-solid fa-palette',
    support: 'fa-solid fa-headset',
  };

  const value = cleanValue(icon);
  return icons[value] || value || 'fa-solid fa-circle-check';
}

function cleanValue(value) {
  return typeof value === 'string' ? value.trim() : value;
}

function localizedValue(slide, field, language) {
  return cleanValue(slide[`${field}_${language}`])
    || cleanValue(slide[`${field}_en`])
    || cleanValue(slide[field])
    || '';
}

export function getSlideContent(slide, language) {
  return {
    headline: localizedValue(slide, 'headline', language),
    subheading: localizedValue(slide, 'subheading', language),
    bullet1Text: localizedValue(slide, 'bullet1_text', language),
    bullet2Text: localizedValue(slide, 'bullet2_text', language),
    primaryText: localizedValue(slide, 'cta_primary_text', language),
    primaryLink: cleanValue(slide.cta_primary_link) || '#products',
    secondaryText: localizedValue(slide, 'cta_secondary_text', language),
    secondaryLink: cleanValue(slide.cta_secondary_link) || '#quote',
  };
}

function HeroBullet({ icon, text }) {
  if (!text) return null;

  const iconValue = cleanValue(icon);
  const isImage = /^https?:\/\//i.test(iconValue);

  return (
    <div className="box_desription_inside">
      {isImage
        ? <img alt="" aria-hidden="true" className="box_icon" src={iconValue} />
        : <i aria-hidden="true" className={`${iconClass(iconValue)} box_icon`} />}
      <span>{text}</span>
    </div>
  );
}

export function Hero() {
  const { language } = useLanguage();
  const { openContact } = useContactForm();
  const [slides, setSlides] = useState(fallbackSlides);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let active = true;

    async function loadSlides() {
      const { data, error } = await supabaseClient
        .from('hero_slides')
        .select(`
          id,
          image_url,
          bullet1_icon,
          bullet2_icon,
          cta_primary_link,
          cta_secondary_link,
          headline_en,
          headline_ar,
          subheading_en,
          subheading_ar,
          bullet1_text_en,
          bullet1_text_ar,
          bullet2_text_en,
          bullet2_text_ar,
          cta_primary_text_en,
          cta_primary_text_ar,
          cta_secondary_text_en,
          cta_secondary_text_ar
        `)
        .eq('is_active', true)
        .order('id', { ascending: true });

      if (active && !error && data?.length) {
        setSlides(data);
        setActiveIndex(0);
      }
    }

    loadSlides();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (slides.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, 6000);

    return () => window.clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="hero">
      <div className="hero-carousel" aria-live="polite">
        {slides.map((slide, index) => {
          const content = getSlideContent(slide, language);

          return (
            <div
              className={`hero-slide${index === activeIndex ? ' active' : ''}`}
              key={slide.id ?? `${content.headline}-${index}`}
              style={{ backgroundImage: `url("${cleanValue(slide.image_url) || asset('assets/hero.png')}")` }}
            >
              <div className="hero_box">
                <div className="box_title">
                  {content.subheading && <p>{content.subheading}</p>}
                  <h1>{content.headline}</h1>
                </div>
                {(content.bullet1Text || content.bullet2Text) && (
                  <div className="box_desription">
                    <HeroBullet icon={slide.bullet1_icon} text={content.bullet1Text} />
                    <HeroBullet icon={slide.bullet2_icon} text={content.bullet2Text} />
                  </div>
                )}
                <div className="box_buttons">
                  {content.primaryText && (
                    <a className="btn2" href={content.primaryLink}>
                      <span>{content.primaryText}</span>
                      <img src={asset('assets/arrow.svg')} alt="" className="btn-arrow" />
                    </a>
                  )}
                  {content.secondaryText && (
                    <button className="btn3" onClick={openContact} type="button">
                      <img src={asset('assets/email.svg')} alt="" />
                      <span>{content.secondaryText}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {slides.length > 1 && (
        <div className="hero-dots" aria-label="Hero slides">
          {slides.map((slide, index) => (
            <button
              aria-label={`Show slide ${index + 1}`}
              className={`hero-dot${index === activeIndex ? ' active' : ''}`}
              key={slide.id ?? index}
              onClick={() => setActiveIndex(index)}
              type="button"
            />
          ))}
        </div>
      )}
    </section>
  );
}
